import { type TextLayer } from '@meitu/saas-editor-core/es/layers/text-layer';
import { type ITextLayerProps } from '@meitu/saas-editor-core/es/version-types';
import { FontSizeSelector } from '@/components/font-size-selector';
import { useEditor } from '@/hooks/use-editor';
import { useGlobalWidget } from '@/hooks/use-global-widget';
import { useTextRangeAttrsState } from '@/hooks/use-text-range-attrs-state';
import { useLayerProps } from '../../hooks';
import { WidgetCommonProps } from '../../types';
import { Tracker } from '../../utils';

type Props = WidgetCommonProps;

const FALLBACK_FONT_SIZE = 6;

export const FontSize: React.FC<Props> = ({ layerId, recordId }) => {
    const editor = useEditor();
    const textRangeAttrsState = useTextRangeAttrsState(layerId);

    const props = useLayerProps(editor, (layer) => {
        if (!layer) return null;

        const layerData = layer.data as ITextLayerProps;
        return {
            fontSize: layerData.fontSize,
            isOnTextEditing: layer.__isOnTextEditing || false,
            advancedTextMaterial: layerData.advancedTextMaterial,
        };
    });

    const fontSize = props?.fontSize;
    const isOnTextEditing = props?.isOnTextEditing || false;
    const selectionFontSize =
        textRangeAttrsState?.type === 'uniform' || textRangeAttrsState?.type === 'mixed'
            ? textRangeAttrsState.attrs.fontSize
            : null;

    const popoverWidgetRef = useGlobalWidget(editor, false);

    const finalFontSize = (() => {
        if (selectionFontSize) return selectionFontSize;
        return fontSize ? fontSize : FALLBACK_FONT_SIZE;
    })();

    const handleOpenChange = (open: boolean) => {
        popoverWidgetRef.current = open;
    };

    const handleChange = (value: number, options = { writeHistory: false }) => {
        editor.getInstance<TextLayer>('TextLayer').setFontSize(layerId, value, options);

        if (options.writeHistory) {
            Tracker.setFontSize({
                recordId,
                stageType: editor.services.editor.getCanvasType(),
                fontSize: value,
            });
        }
    };

    return (
        <FontSizeSelector
            value={finalFontSize}
            disableMouseWheel={isOnTextEditing}
            onChange={(value) => handleChange(value, { writeHistory: false })}
            onAfterChange={(value) => handleChange(value, { writeHistory: true })}
            onOpenChange={handleOpenChange}
        />
    );
};
